import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { EnvelopeIcon, MapPinIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import {
  hasRichTextContent,
  memberEducationToHtml,
} from '../utils/richText';
import { memberGroupLabel } from '../constants/memberGroups';
import MediaImage from '../components/MediaImage';
import { normalizeHtmlMediaUrls } from '../utils/media';

type Member = {
  _id: string;
  name: string;
  title?: string;
  group?: string;
  email?: string;
  location?: string;
  homepage?: string;
  avatarUrl?: string;
  researchDirections?: string[];
  education?: string | { degree?: string; institution?: string; year?: string }[];
  bio?: string;
};

type MemberPublication = {
  _id: string;
  title: string;
  authors: string;
  venue?: string;
  year?: number;
  url?: string;
};

const renderHtml = (html?: string) => ({ __html: html || '' });

const formatHomepageUrl = (url: string) => {
  const trimmed = url.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const MemberDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);
  const [pubs, setPubs] = useState<MemberPublication[]>([]);
  const [loadingPubs, setLoadingPubs] = useState(true);

  useEffect(() => {
    const fetchMember = async () => {
      if (!id) return;
      try {
        const res = await api.get<Member>(`/members/${id}`);
        setMember(res.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchMember();
  }, [id]);

  useEffect(() => {
    const fetchPubs = async () => {
      if (!id) return;
      try {
        setLoadingPubs(true);
        const res = await api.get<MemberPublication[]>(
          `/publications?memberId=${id}`
        );
        setPubs(res.data || []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingPubs(false);
      }
    };
    fetchPubs();
  }, [id]);

  if (loading) {
    return (
      <section className="bg-surface min-h-full">
        <div className="mx-auto max-w-screen-xl px-8 py-20">
          <p className="text-sm text-on-surface-variant">Loading member information...</p>
        </div>
      </section>
    );
  }

  if (!member) {
    return (
      <section className="bg-surface min-h-full">
        <div className="mx-auto max-w-screen-xl px-8 py-20">
          <p className="text-sm text-on-surface-variant">Member not found.</p>
          <Link
            to="/members"
            className="mt-4 inline-block text-sm text-primary underline"
          >
            Back to members list
          </Link>
        </div>
      </section>
    );
  }

  const selectedPubs = pubs
    .slice()
    .sort((a, b) => (b.year || 0) - (a.year || 0))
    .slice(0, 3);

  const educationHtml = memberEducationToHtml(member.education);

  return (
    <section className="bg-surface min-h-full">
      <div className="mx-auto max-w-screen-2xl px-8 pb-20 pt-12">
        <div className="mb-8 flex items-center gap-2 text-sm text-on-surface-variant">
          <Link to="/" className="hover:text-primary">Home</Link>
          <span>/</span>
          <Link to="/members" className="hover:text-primary">Members</Link>
          <span>/</span>
          <span className="text-primary">{member.name}</span>
        </div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-20">
          <aside className="space-y-6 lg:col-span-4">
            <div className="um-card overflow-hidden bg-surface-container-low aspect-[3/4]">
              {member.avatarUrl ? (
                <MediaImage
                  src={member.avatarUrl}
                  alt={member.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-surface-container text-7xl font-semibold text-primary">
                  {member.name.charAt(0)}
                </div>
              )}
            </div>

            <div className="um-card bg-surface-container-lowest p-6">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-on-surface-variant">Contact</h3>
              <div className="mt-4 space-y-3 text-sm text-on-surface">
                {member.email && (
                  <div className="flex items-center gap-3">
                    <EnvelopeIcon className="h-5 w-5 shrink-0 text-primary" />
                    <a href={`mailto:${member.email}`} className="hover:underline">
                      {member.email}
                    </a>
                  </div>
                )}
                {member.location?.trim() && (
                  <div className="flex items-start gap-3">
                    <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <span>{member.location.trim()}</span>
                  </div>
                )}
                {member.homepage?.trim() && (
                  <div className="flex items-center gap-3">
                    <GlobeAltIcon className="h-5 w-5 shrink-0 text-primary" />
                    <a
                      href={formatHomepageUrl(member.homepage)}
                      target="_blank"
                      rel="noreferrer"
                      className="break-all hover:underline"
                    >
                      {member.homepage.trim()}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </aside>

          <div className="space-y-10 lg:col-span-8">
            <header className="space-y-3">
              <h1 className="font-serif text-5xl text-primary md:text-6xl">{member.name}</h1>
              <p className="text-xl text-primary-container">{member.title || 'Research Group Member'}</p>
              <p className="text-base text-on-surface-variant">
                {member.group ? memberGroupLabel(member.group) : 'Research Team'}
              </p>
            </header>

            <section>
              <h3 className="text-xs uppercase tracking-widest text-on-surface-variant">Research Directions</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {(member.researchDirections || []).map((tag) => (
                  <span key={tag} className="rounded-full bg-secondary-container px-3 py-1 text-xs text-on-secondary-container">
                    {tag}
                  </span>
                ))}
                {(member.researchDirections || []).length === 0 && (
                  <span className="rounded-full bg-surface-container px-3 py-1 text-xs text-on-surface-variant">
                    No tags
                  </span>
                )}
              </div>
            </section>

            <section className="um-card bg-surface-container-low p-8">
              {hasRichTextContent(member.bio) ? (
                <div
                  className="rich-text text-on-surface leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: normalizeHtmlMediaUrls(member.bio || ''),
                  }}
                />
              ) : (
                <p className="leading-relaxed text-on-surface-variant">
                  Research bio will be updated soon.
                </p>
              )}
            </section>

            <section className="space-y-6">
              <h2 className="border-b border-surface-variant pb-4 font-serif text-4xl text-primary">Education</h2>
              {hasRichTextContent(educationHtml) ? (
                <div
                  className="rich-text text-on-surface leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: normalizeHtmlMediaUrls(educationHtml),
                  }}
                />
              ) : (
                <p className="text-on-surface-variant">No education information yet.</p>
              )}
            </section>
          </div>
        </div>

        <section className="um-card mt-16 bg-surface-container-low p-8 md:p-12">
          <div className="mb-6 flex items-end justify-between">
            <h2 className="font-serif text-4xl text-primary">Selected Outputs</h2>
            <Link to="/publications" className="um-btn-link text-sm">View full publications list</Link>
          </div>
          {loadingPubs ? (
            <p className="text-on-surface-variant">Loading publications...</p>
          ) : selectedPubs.length === 0 ? (
            <p className="text-on-surface-variant">No linked publications yet.</p>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {selectedPubs.map((pub) => (
                <article key={pub._id} className="um-card bg-surface-container-lowest p-6">
                  <p className="text-xs uppercase tracking-widest text-primary-container">
                    Output · {pub.year || 'Year N/A'}
                  </p>
                  <h3 className="mt-3 font-serif text-2xl leading-tight text-on-surface">
                    <Link to={`/publications/${pub._id}`} className="hover:text-primary">
                      <span dangerouslySetInnerHTML={renderHtml(pub.title)} />
                    </Link>
                  </h3>
                  {pub.venue ? (
                    <p
                      className="mt-4 text-sm text-on-surface-variant"
                      dangerouslySetInnerHTML={renderHtml(pub.venue)}
                    />
                  ) : (
                    pub.authors && (
                      <p
                        className="mt-4 text-sm text-on-surface-variant"
                        dangerouslySetInnerHTML={renderHtml(pub.authors)}
                      />
                    )
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </section>
  );
};

export default MemberDetail;

